import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { ReactComponent as CalendarIcon } from "feather-icons/dist/icons/calendar.svg";
import { ReactComponent as ClockIcon } from "feather-icons/dist/icons/clock.svg";
import { ReactComponent as ArrowRightIcon } from "feather-icons/dist/icons/arrow-right.svg";

import CoverArt from "./CoverArt";
import { CategoryBadge, MetaRow, MetaItem, Eyebrow } from "./ui";
import Avatar from "./Avatar";
import { formatDate, readingTimeLabel } from "../utils/text";

/** L'article « à la une » : grande carte horizontale en tête de l'index. */

const Card = styled(Link)`
  ${tw`flex flex-col lg:flex-row bg-gray-100 rounded-lg overflow-hidden focus:outline-none focus:shadow-outline`}
  transition: transform 400ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 400ms ease;
  &:hover {
    ${tw`shadow-raised`}
    transform: translateY(-4px);
  }
`;

const ImageWrap = styled.div`
  ${tw`relative w-full h-64 sm:h-80 lg:h-auto lg:w-1/2 overflow-hidden bg-gray-200`}
  min-height: 16rem;
`;

const Info = tw.div`flex-1 flex flex-col justify-center p-8 sm:p-10 lg:p-12 border-2 border-t-0 lg:border-t-2 lg:border-l-0 border-gray-200 rounded-b-lg lg:rounded-b-none lg:rounded-r-lg`;
const TopRow = tw.div`flex items-center flex-wrap`;
const Label = tw(Eyebrow)`mr-4 mb-2`;
const Title = tw.h2`mt-5 font-black text-2xl sm:text-3xl lg:text-4xl leading-tight text-gray-900 group-hover:text-primary-500 transition duration-300`;
const Excerpt = tw.p`mt-4 text-base lg:text-lg text-gray-600 font-medium leading-relaxed`;
const Meta = tw(MetaRow)`mt-6`;
const Author = tw.div`mt-6 flex items-center`;
const Portrait = tw(Avatar)`mr-3`;
const AuthorName = tw.span`text-sm font-bold text-gray-800`;
const AuthorRole = tw.span`block text-xs text-gray-600 font-medium`;
const ReadMore = styled.span`
  ${tw`mt-8 inline-flex items-center font-bold text-primary-500`}
  svg {
    ${tw`w-5 h-5 ml-2 transition duration-300 transform`}
  }
  ${Card}:hover & svg {
    ${tw`translate-x-1`}
  }
`;

export default function FeaturedPost({ post }) {
  if (!post) return null;
  const { author } = post;
  return (
    <Card to={`/blog/${post.slug}`} aria-label={post.title} className="group">
      <ImageWrap>
        <CoverArt seed={post.slug} color={post.category.color} image={post.cover.image} plain={post.cover.plain} alt="" eager />
      </ImageWrap>
      <Info>
        <TopRow>
          <Label>À la une</Label>
          <CategoryBadge category={post.category} linked={false} />
        </TopRow>
        <Title>{post.title}</Title>
        {post.excerpt && <Excerpt>{post.excerpt}</Excerpt>}
        <Meta>
          <MetaItem>
            <CalendarIcon aria-hidden="true" />
            <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
          </MetaItem>
          <MetaItem>
            <ClockIcon aria-hidden="true" />
            {readingTimeLabel(post.readingTime)}
          </MetaItem>
        </Meta>
        {author && (
          <Author>
            <Portrait name={author.name} image={author.image} size={40} />
            <span>
              <AuthorName>{author.name}</AuthorName>
              {author.role && <AuthorRole>{author.role}</AuthorRole>}
            </span>
          </Author>
        )}
        <ReadMore>
          Lire l'article <ArrowRightIcon aria-hidden="true" />
        </ReadMore>
      </Info>
    </Card>
  );
}
